import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { db } from "../firebase.config";
import { doc, updateDoc, collection, addDoc, Timestamp, getDoc, runTransaction } from "firebase/firestore";

// Helper function to add page numbers to PDF
function addPageNumbers(pdf) {
  const pageCount = pdf.getNumberOfPages();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageNumberY = pageHeight - 10; // Position near bottom
  
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    const pageText = `${i} of ${pageCount}`;
    pdf.text(pageText, pageWidth / 2, pageNumberY, { align: 'center' });
  }
}

// Helper function to get formatted timestamp with timezone
function getFormattedTimestamp() {
  const date = new Date();
  const options = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  };
  return date.toLocaleString('en-US', options);
}

// Helper to convert a number like 45.26 to 'forty five rupees and twenty six paise'
function numberToRupeesPaiseWords(amount) {
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  let words = '';
  if (rupees > 0) {
    words +=  ' rupees ' + numberToWords(rupees);
  }
  if (paise > 0) {
    if (words) words += ' and ';
    words +=  'paise ' + numberToWords(paise);
  }
  if (!words) words = 'zero rupees';
  return words;
}

// Helper to convert number to words with currency
function numberToWordsWithCurrency(amount, currencyLabel) {
  const rounded = Math.round(amount);
  const words = numberToWords(rounded);
  return `${words.charAt(0).toUpperCase() + words.slice(1)} ${currencyLabel}`;
}

// Simple number to words (supports up to 99999999)
// This function converts a number into words using the Indian numbering system.

// Its fundamental logic is to process the number iteratively from right to left, breaking it down into 3-digit chunks. A loop runs as long as there are digits left to process.

// Inside the loop, it first isolates the last three digits. This chunk is then converted into its word equivalent by handling the hundreds place, followed by the tens and units. The function then identifies the correct positional name for that chunk (like "thousand", "lakh", etc.) and prepends the chunk's words and its positional name to the beginning of the final result string.

// After a chunk is processed, its digits are removed from the number, and the loop continues with the next 3-digit chunk to the left. This continues until the entire number is converted. Finally, it performs a quick cleanup to remove extra spaces from the output.

function numberToWords(num) {
  const a = [ '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen' ];
  const b = [ '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety' ];
  const g = [ '', 'thousand', 'lakh', 'crore' ];
  if (num === 0) return 'zero';
  let str = '';
  let i = 0;
  while (num > 0) {
    let chunk = num % 1000;
    if (chunk) {
      let chunkStr = '';
      if (chunk > 99) {
        chunkStr += a[Math.floor(chunk / 100)] + ' hundred ';
        chunk %= 100;
      }
      if (chunk > 19) {
        chunkStr += b[Math.floor(chunk / 10)] + ' ' + a[chunk % 10] + ' ';
      } else if (chunk > 0) {
        chunkStr += a[chunk] + ' ';
      }
      str = chunkStr + g[i] + ' ' + str;
    }
    num = Math.floor(num / 1000);
    i++;
  }
  return str.replace(/ +/g, ' ').trim();
}

// Unified PDF generator for both invoice and quotation
export function generateDocumentPDF(options) {
  // If options.type is 'invoice' or has invoiceNumber, use invoice logic
  if (options.type === 'invoice' || options.invoiceNumber || options.invoiceId) {
    return generateInvoicePDF(options);
  }
  // If options.type is 'quotation' or has quotationNumber, use quotation logic
  if (options.type === 'quotation' || options.quotationNumber || options.quotationId) {
    return generateQuotationPDF(options);
  }
  throw new Error('Unknown document type for PDF generation');
}

// Helper function to add banner section to invoice PDF
function addInvoiceBannerSection(pdf, banner, businessName, businessAddress, currentY, margin, contentWidth, pageWidth) {
  if (banner && banner.length > 0 && typeof banner === 'string') {
    try {
      console.log("Banner found for invoice, attempting to add");
      const bannerHeight = 40; // Height of banner in mm
      const bannerWidth = contentWidth;
      
      // Detect image format based on data URL
      let imageFormat = 'PNG'; // Default format
      if (banner.includes('data:image/jpeg') || banner.includes('data:application/jpeg')) {
        imageFormat = 'JPEG';
      } else if (banner.includes('data:image/png')) {
        imageFormat = 'PNG';
      } else if (banner.includes('data:image/webp')) {
        imageFormat = 'WEBP';
      } else if (banner.includes('data:image/')) {
        const match = banner.match(/data:image\/([^;]+)/);
        if (match) {
          imageFormat = match[1].toUpperCase();
        }
      }
      
      console.log("Adding invoice banner with format:", imageFormat);
      pdf.addImage(banner, imageFormat, margin, currentY, bannerWidth, bannerHeight);
      console.log("Invoice banner added successfully");
      currentY += bannerHeight + 5;
      
      // Add business name and address below the banner only if they are provided
      if (businessName && businessName.trim()) {
        pdf.setFontSize(11).setFont("helvetica", "bold");
        pdf.text(String(businessName), margin, currentY);
        currentY += 6;
      }
      if (businessAddress && businessAddress.trim()) {
        pdf.setFontSize(8).setFont("helvetica", "normal");
        const addressLines = pdf.splitTextToSize(businessAddress, contentWidth / 2);
        pdf.text(addressLines.map(line => String(line)), margin, currentY);
        currentY += addressLines.length * 4 + 5;
      } else {
        currentY += 3; // Add minimal spacing if no address
      }
    } catch (e) {
      console.error("Error adding banner to invoice PDF:", e);
      // Fallback: show business info without banner if provided
      if (businessName && businessName.trim()) {
        pdf.setFontSize(11).setFont("helvetica", "bold");
        pdf.text(String(businessName), pageWidth - margin, currentY, { align: 'right' });
        pdf.setFontSize(8).setFont("helvetica", "normal");
        if (businessAddress && businessAddress.trim()) {
          const addressLines = pdf.splitTextToSize(businessAddress, 70);
          pdf.text(addressLines.map(line => String(line)), pageWidth - margin, currentY + 6, { align: 'right' });
          currentY += 15;
        } else {
          currentY += 10;
        }
      }
    }
  }
  return currentY;
}

export function generateInvoicePDF(options) {
  const {
    businessName,
    businessAddress,
    logo,
    banner,
    invoiceNumber,
    invoiceDate,
    validUntil,
    billTo: billToRaw,
    shipTo: shipToRaw,
    items = [],
    totals = {},
    currencySymbol,
    additionalNotes,
    termsAndConditions,
    coveringLetter,
    authorizedSignature,
    authorizedSignatureName,
    customerSignatureName,
    invoiceFooter,
    isSaved,
    setIsPrinting,
    invoiceType,
    showGST = true, // Add GST toggle flag with default true for backward compatibility
  } = options;

  // Helper function to convert currency symbol to code
  const getCurrencyLabel = () => {
    if (currencySymbol === '₹' || currencySymbol === '\u20B9' || currencySymbol === 'Rs') return 'INR';
    if (currencySymbol === '$') return 'USD';
    if (currencySymbol === '€') return 'EUR';
    if (currencySymbol === '£') return 'GBP';
    if (currencySymbol === '¥') return 'JPY';
    return (typeof currencySymbol === 'string' && currencySymbol.length === 3) ? currencySymbol : 'INR';
  };

  // Safe defaults for billTo and shipTo
  // Ensure billTo and shipTo are objects with string fields
  const billTo = {
    name: (billToRaw && typeof billToRaw.name === 'string') ? billToRaw.name : '',
    address: (billToRaw && typeof billToRaw.address === 'string') ? billToRaw.address : ''
  };
  const shipTo = {
    name: (shipToRaw && typeof shipToRaw.name === 'string') ? shipToRaw.name : '',
    address: (shipToRaw && typeof shipToRaw.address === 'string') ? shipToRaw.address : ''
  };
  if (setIsPrinting) setIsPrinting(true);
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let currentY = margin;
  const addSection = (title, text, options = {}) => {
    const { spaceAfter = 8, titleSize = 11, textSize = 10 } = options;
    const plainText = htmlToPlainText(text);
    if (!plainText || !plainText.trim()) return;
    const pageBottomMargin = 25;
    const titleSpacing = 6;
    const titleHeight = (title && title.trim()) ? (pdf.setFontSize(titleSize).getTextDimensions(title).h + titleSpacing) : 0;
    pdf.setFontSize(textSize);
    const textLines = pdf.splitTextToSize(plainText, contentWidth);
    const textBlockHeight = pdf.getTextDimensions(textLines).h;
    const totalSectionHeight = titleHeight + textBlockHeight;
    if (currentY + totalSectionHeight > pageHeight - pageBottomMargin) {
      pdf.addPage();
      currentY = margin;
    }
    if (title && title.trim()) {
      pdf.setFontSize(titleSize).setFont("helvetica", "bold");
      pdf.text(title, margin, currentY);
      currentY += titleSpacing;
    }
    pdf.setFontSize(textSize).setFont("helvetica", "normal");
    pdf.text(textLines, margin, currentY);
    currentY += textBlockHeight + spaceAfter;
  };
  const hasCoveringLetter = coveringLetter && htmlToPlainText(coveringLetter).trim();
  if (hasCoveringLetter) {
    pdf.setFontSize(13).setFont('helvetica', 'bold');
    pdf.text('Covering Letter', margin, currentY);
    currentY += 8;
    pdf.setFontSize(10).setFont('helvetica', 'normal');
    const senderDetails = [
      businessName || "Your Business Name",
      ...(businessAddress ? businessAddress.split('\n') : ["Your Business Address"]),
    ];
    let senderY = currentY;
    senderDetails.forEach(line => {
      pdf.text(line, pageWidth - margin, senderY, { align: 'right' });
      senderY += 5;
    });
    currentY = Math.max(currentY + 25, senderY);
    pdf.text(new Date(invoiceDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }), margin, currentY);
    currentY += 15;
    pdf.setFontSize(10).setFont('helvetica', 'bold');
    pdf.text("To,", margin, currentY);
    currentY += 5;
    pdf.setFont('helvetica', 'normal');
    const recipientDetails = [
      billTo.name,
      ...(billTo.address ? billTo.address.split('\n') : [])
    ];
    recipientDetails.forEach(line => {
      if (line.trim()) {
        pdf.text(line, margin, currentY);
        currentY += 5;
      }
    });
    currentY += 10;
    pdf.setFontSize(10).setFont('helvetica', 'bold');
    pdf.text("Subject: Submission of Invoice", margin, currentY);
    currentY += 15;
    pdf.setFont('helvetica', 'normal');
    const recipientName = billTo.name.split('\n')[0];
    pdf.text(`Dear ${recipientName || 'Sir/Madam'},`, margin, currentY);
    currentY += 10;
    const plainTextLetter = htmlToPlainText(coveringLetter);
    const letterLines = pdf.splitTextToSize(plainTextLetter, contentWidth);
    pdf.text(letterLines, margin, currentY);
    currentY += letterLines.length * 5 + 10;
    pdf.text("Sincerely,", margin, currentY);
    currentY += 10;
    if (authorizedSignature) {
      try {
        pdf.addImage(authorizedSignature, 'PNG', margin, currentY, 40, 20);
        currentY += 25;
      } catch(e) { console.error("Could not add signature to covering letter", e); }
    }
    pdf.setFont('helvetica', 'bold');
    pdf.text(authorizedSignatureName || "Authorized Signatory", margin, currentY);
    currentY += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.text(businessName || "Your Business Name", margin, currentY);
    pdf.addPage();
    currentY = margin;
  }
  pdf.setFontSize(18).setFont("helvetica", "bold");
  let invoiceTitle = 'INVOICE';
  if (invoiceType === 'tax') invoiceTitle = 'TAX INVOICE';
  else if (invoiceType === 'proforma') invoiceTitle = 'PROFORMA INVOICE';
  pdf.text(invoiceTitle, pageWidth / 2, currentY, { align: 'center' });
  currentY += 15;
  
  // Add banner if present
  currentY = addInvoiceBannerSection(pdf, banner, businessName, businessAddress, currentY, margin, contentWidth, pageWidth);
  
  const detailsBlockY = currentY;
  const colW = (contentWidth / 3) - 5;
  const colGap = 10;
  const col1X = margin;
  const col2X = col1X + colW + colGap;
  const col3X = col2X + colW + colGap;
  pdf.setFontSize(9).setFont("helvetica", "bold");
  pdf.text("Bill To:", col1X, detailsBlockY);
  pdf.setFont("helvetica", "normal");
  const billToLines = pdf.splitTextToSize(`${billTo.name || ''}\n${billTo.address || ''}`, colW);
  pdf.text(billToLines.map(line => String(line)), col1X, detailsBlockY + 5);
  const billToHeight = billToLines.length * 5 + 10;
  pdf.setFontSize(10).setFont("helvetica", "bold");
  pdf.text("Ship To:", col2X, detailsBlockY);
  pdf.setFont("helvetica", "normal");
  const shipToLines = pdf.splitTextToSize(`${shipTo.name || ''}\n${shipTo.address || ''}`, colW);
  pdf.text(shipToLines.map(line => String(line)), col2X, detailsBlockY + 5);
  const shipToHeight = shipToLines.length * 5 + 10;
  let metaY = detailsBlockY;
  const metaLabelGap = 28;
  pdf.setFontSize(9).setFont("helvetica", "bold");
  pdf.text("Invoice ID:", col3X, metaY);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(invoiceNumber || ''), col3X + metaLabelGap, metaY);
  metaY += 5;
  pdf.setFont("helvetica", "bold");
  pdf.text("Date:", col3X, metaY);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(invoiceDate || ''), col3X + metaLabelGap, metaY);
  metaY += 5;
  pdf.setFont("helvetica", "bold");
  pdf.text("Valid Until:", col3X, metaY);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(validUntil || ''), col3X + metaLabelGap, metaY);
  metaY += 5;
  pdf.setFont("helvetica", "bold");
  pdf.text("Currency:", col3X, metaY);
  pdf.setFont("helvetica", "normal");
  const currencyLabel = getCurrencyLabel();
  pdf.text(String(currencyLabel || 'INR'), col3X + metaLabelGap, metaY);
  metaY += 5;
  const metaHeight = metaY - detailsBlockY + 10;
  const blockHeight = Math.max(billToHeight, shipToHeight, metaHeight) + 5;
  currentY = detailsBlockY + blockHeight;
  
  // Check if any item has a discount
  const hasAnyDiscount = items.some(item => item.discount && parseFloat(item.discount) > 0);
  
  // Build table headers dynamically based on whether discount exists
  const tableHeadRow = ["Product Name", "Qty", `Unit Price (${currencyLabel})`];
  if (hasAnyDiscount) {
    tableHeadRow.push("Discount");
  }
  tableHeadRow.push("Tax", `Total (${currencyLabel})`);
  const tableHead = [tableHeadRow];
  
  // Build table body with conditional discount column
  const tableBody = items.map(item => {
    const row = [
      item.description,
      item.quantity,
      formatTableCurrency(item.unitPrice, currencySymbol),
    ];
    if (hasAnyDiscount) {
      row.push(`${item.discount}%`);
    }
    row.push(`${item.tax}%`, formatTableCurrency(item.total, currencySymbol));
    return row;
  });
  
  // Build total rows with conditional discount column
  const subtotalRow = ['', '', ''];
  if (hasAnyDiscount) {
    subtotalRow.push('');
  }
  subtotalRow.push('Subtotal:', formatTableCurrency(totals.subtotal, currencySymbol));
  
  const gstRow = ['', '', ''];
  if (hasAnyDiscount) {
    gstRow.push('');
  }
  gstRow.push('Total GST:', formatTableCurrency(totals.totalTaxAmount, currencySymbol));
  
  const grandTotalRow = ['', '', ''];
  if (hasAnyDiscount) {
    grandTotalRow.push('');
  }
  grandTotalRow.push('Grand Total:', `${formatTableCurrency(totals.grandTotal, currencySymbol)}`);
  
  const fullTableBody = [...tableBody, subtotalRow, gstRow, grandTotalRow];
  autoTable(pdf, {
    startY: currentY,
    head: tableHead,
    body: fullTableBody,
    theme: 'grid',
    headStyles: { fillColor: [38, 100, 200], textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: margin, right: margin },
    didParseCell: function(data) {
      const isTotalRow = data.row.index >= items.length;
      if (isTotalRow) {
        // Column indices need to be adjusted based on whether discount column exists
        const priceColIndex = hasAnyDiscount ? 5 : 4;
        const totalColIndex = hasAnyDiscount ? 6 : 5;
        if (data.column.index === priceColIndex || data.column.index === totalColIndex) {
          data.cell.styles.halign = 'right';
        }
        if (data.row.index === fullTableBody.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [38, 100, 200];
          data.cell.styles.fillColor = [230, 240, 255];
        }
      }
    }
  });
  currentY = pdf.lastAutoTable.finalY + 10;
  // Add grand total in words before the summary, right-aligned and not bold
  if (typeof totals.grandTotal === 'number' && !isNaN(totals.grandTotal)) {
    const grandTotalWords = numberToRupeesPaiseWords(totals.grandTotal);
    // Capitalize first letter of every word
    const grandTotalWordsTitleCase = grandTotalWords.replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1));
    pdf.setFontSize(11).setFont('helvetica', 'normal');
    // Build the full text and wrap it to the content width so it doesn't overflow the page
    const fullWordsText = `Grand Total (in words): ${grandTotalWordsTitleCase} Only`;
    const wrappedLines = pdf.splitTextToSize(fullWordsText, contentWidth);
    const blockHeight = pdf.getTextDimensions(wrappedLines).h || (wrappedLines.length * 6);
    const pageBottomMargin = 25;
    if (currentY + blockHeight > pageHeight - pageBottomMargin) {
      pdf.addPage();
      currentY = margin;
    }
    // Draw each wrapped line right-aligned to the page's content area
    const lineHeight = blockHeight / wrappedLines.length;
    wrappedLines.forEach(line => {
      pdf.text(line, pageWidth - margin, currentY, { align: 'right' });
      currentY += lineHeight;
    });
    // add a small spacer after the block
    currentY += 2;
  }
  // Render summary table with only required columns for Invoice
  if (Array.isArray(options.summary) && options.summary.length > 0) {
    // Extract specific values from summary for cleaner table
    const getValueFromSummary = (labels) => {
      for (let label of labels) {
        const item = options.summary.find(s => s.label.toLowerCase().includes(label.toLowerCase()));
        if (item) {
          let value = String(item.value);
          value = value.replace(/([\u20b9$\u20ac\u00a3\u00a5]|INR|USD|EUR|GBP|JPY|-)/gi, '').trim();
          return value;
        }
      }
      return '0';
    };

    const summaryHead = [['Discount', 'Taxable Amt (INR)', 'CGST', 'SGST', 'IGST', 'Net Total (INR)']];
    const summaryBody = [[
      getValueFromSummary(['discount']),
      getValueFromSummary(['taxable']),
      getValueFromSummary(['cgst']),
      getValueFromSummary(['sgst']),
      getValueFromSummary(['igst']),
      getValueFromSummary(['net total', 'grand total'])
    ]];

    autoTable(pdf, {
      startY: currentY,
      head: summaryHead,
      body: summaryBody,
      theme: 'grid',
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 4, halign: 'left', valign: 'middle' },
      headStyles: { fillColor: [63, 81, 181], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'left', valign: 'middle' },
      bodyStyles: { fontStyle: 'normal', halign: 'left', valign: 'middle' },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { halign: 'left' },
        4: { halign: 'left' },
        5: { halign: 'left' },
      },
    });
    currentY = pdf.lastAutoTable.finalY + 8;
  }
  addSection("Additional Notes", additionalNotes, { spaceAfter: 4 });
  addSection("Terms and Conditions", termsAndConditions, { spaceAfter: 15 });
  // Only show signature section if at least one signature is present
  const showSignatureSection = authorizedSignature || (customerSignatureName && customerSignatureName.trim());
  if (showSignatureSection) {
    const signatureSectionHeight = 55;
    if (currentY > pageHeight - signatureSectionHeight - 20) { 
      pdf.addPage();
      currentY = margin;
    }
    pdf.setFontSize(11).setFont("helvetica", "bold");
    pdf.text("Signatures", margin, currentY);
    currentY += 10;
    const sigWidth = 60;
    const sigHeight = 30;
    const signatureY = currentY + 5; 
    if (authorizedSignature) {
      try {
        pdf.addImage(authorizedSignature, 'PNG', margin, signatureY, sigWidth, sigHeight);
        pdf.setLineWidth(0.2).line(margin, signatureY + sigHeight, margin + sigWidth, signatureY + sigHeight);
        pdf.setFontSize(8).setFont("helvetica", "normal");
        pdf.text(authorizedSignatureName || "Authorized Signature", margin + sigWidth / 2, signatureY + sigHeight + 5, { align: 'center' });
      } catch (e) {
        console.error("Could not add authorized signature to PDF:", e);
        pdf.setFontSize(9).setFont("helvetica", "italic");
        pdf.text("Error rendering authorized signature.", margin, signatureY + 15);
      }
    }
    if (customerSignatureName && customerSignatureName.trim()) {
      const customerSigX = pageWidth - margin - sigWidth;
      pdf.setLineWidth(0.2).line(customerSigX, signatureY + sigHeight, customerSigX + sigWidth, signatureY + sigHeight);
      pdf.setFontSize(8).setFont("helvetica", "normal");
      pdf.text(customerSignatureName, customerSigX + sigWidth / 2, signatureY + sigHeight + 5, { align: 'center' });
    }
    currentY += signatureSectionHeight - 10;
  }
  const plainFooterText = htmlToPlainText(invoiceFooter);
  if (plainFooterText && plainFooterText.trim()) {
    const pageBottomMargin = 25;
    const dividerHeight = 5; 
    pdf.setFontSize(9);
    const footerTextHeight = pdf.getTextDimensions(pdf.splitTextToSize(plainFooterText, contentWidth)).h;
    if (currentY + dividerHeight + footerTextHeight > pageHeight - pageBottomMargin) {
      pdf.addPage();
      currentY = margin;
    }
    currentY += 10;
    pdf.setDrawColor("#cccccc");
    pdf.setLineWidth(0.2).line(margin, currentY, pageWidth - margin, currentY);
    currentY += 8;
    addSection("", invoiceFooter, { spaceAfter: 0 }); 
  }

  // Add formatted timestamp footer with timezone
  const formattedTimestamp = getFormattedTimestamp();
  pdf.setFontSize(7).setFont("helvetica", "italic");
  pdf.text(`Generated on: ${formattedTimestamp}`, margin, pageHeight - 10);
  pdf.text("Powered by OmneVu.com", pageWidth - margin, pageHeight - 10, { align: 'right' });
  pdf.setFont("helvetica", "normal");
  if (options && options.returnBlob) {
    // Return a Blob for preview
    return pdf.output('blob');
  } else {
    let fileName = '';
    if (invoiceType === 'proforma') {
      // Use Proforma-##### for proforma invoice
      const suffix = (invoiceNumber && typeof invoiceNumber === 'string' && invoiceNumber.split('-')[1]) ? invoiceNumber.split('-')[1] : '#####';
      fileName = `Proforma-${suffix}.pdf`;
    } else {
      // Use invoiceNumber for tax invoice and others
      fileName = `${invoiceNumber}.pdf`;
    }
    addPageNumbers(pdf);
    pdf.save(fileName);
    
    // If excess credit note amount exists, handle credit note closure and creation
    if (options.excessCreditNoteAmount && options.excessCreditNoteAmount > 0 && options.selectedCreditNotes && options.userId) {
      setTimeout(() => {
        handleExcessCreditNote({
          businessName: options.businessName,
          businessAddress: options.businessAddress,
          logo: options.logo,
          banner: options.banner,
          invoiceNumber: options.invoiceNumber,
          invoiceDate: options.invoiceDate,
          billTo: options.billTo,
          excessCreditNoteAmount: options.excessCreditNoteAmount,
          currencySymbol: options.currencySymbol,
          invoiceFooter: options.invoiceFooter,
          authorizedSignature: options.authorizedSignature,
          authorizedSignatureName: options.authorizedSignatureName,
          selectedCreditNotes: options.selectedCreditNotes,
          userId: options.userId,
        });
      }, 500); // Delay to avoid browser blocking multiple downloads
    }
    
    if (setIsPrinting) setIsPrinting(false);
    return pdf;
  }
}


// Helper functions (copy from QTemplate.js or import if already available)
function htmlToPlainText(html) {
  if (!html) return "";
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
}

function formatTableCurrency(value, currencySymbol) {
  // Only return the numeric value, no currency symbol or prefix
  let locale = 'en-IN';
  if (currencySymbol === '$') locale = 'en-US';
  else if (currencySymbol === '€') locale = 'en-IE';
  else if (currencySymbol === '£') locale = 'en-GB';
  else if (currencySymbol === '¥') locale = 'ja-JP';
  try {
    return Number(value).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch {
    return Number(value).toLocaleString(locale, { minimumFractionDigits: 2 });
  }
}

// Handle excess credit note: close applied CNs, create new CN for excess, and generate PDF
async function handleExcessCreditNote(options) {
  const {
    businessName,
    businessAddress,
    logo,
    banner,
    invoiceNumber,
    invoiceDate,
    billTo,
    excessCreditNoteAmount,
    currencySymbol,
    invoiceFooter,
    authorizedSignature,
    authorizedSignatureName,
    selectedCreditNotes,
    userId,
    creditNoteReferences,
  } = options;

  try {
    // Get the first/primary credit note to inherit details from
    const primaryCreditNote = selectedCreditNotes && selectedCreditNotes.length > 0 ? selectedCreditNotes[0] : null;

    // Step 1: Close all applied credit notes by updating their status to 'closed'
    for (const creditNote of selectedCreditNotes) {
      const creditNoteRef = doc(db, "users", userId, "creditNotes", creditNote.id);
      await updateDoc(creditNoteRef, { status: "closed" });
    }

    // Step 2: Create a new credit note for the excess amount with 'open' status
    // Generate new credit note number using transaction (atomic operation)
    const userDocRef = doc(db, "users", userId);
    let newCreditNoteNumber = `CN-${Date.now()}`; // Fallback
    let newCounter = 0;

    try {
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userDocRef);
        
        if (userDoc.exists()) {
          newCounter = (userDoc.data()?.creditNoteCounter || 0) + 1;
          const prefix = userDoc.data()?.creditNotePrefix || "CN";
          newCreditNoteNumber = `${prefix}-${newCounter}`;
          
          // Update counter in transaction
          transaction.update(userDocRef, { creditNoteCounter: newCounter });
        } else {
          // If user document doesn't exist, use fallback
          newCreditNoteNumber = `CN-${Date.now()}`;
        }
      });
    } catch (error) {
      console.warn("Could not generate credit note number via transaction, using fallback:", error);
      newCreditNoteNumber = `CN-${Date.now()}`;
    }
    
    const newCreditNoteData = {
      creditNoteNumber: newCreditNoteNumber,
      referenceNumber: primaryCreditNote?.referenceNumber || (creditNoteReferences && primaryCreditNote?.id ? creditNoteReferences[primaryCreditNote.id] : `REF-${Date.now()}-${Math.random().toString(36).substring(2, 9).toUpperCase()}`),
      creditNoteDate: new Date().toISOString().split('T')[0],
      billTo: primaryCreditNote?.billTo || billTo,
      items: [
        {
          description: `Excess Credit Note from Invoice ${invoiceNumber}`,
          unitPrice: excessCreditNoteAmount,
          quantity: 1,
          discount: 0,
          tax: 0,
          total: excessCreditNoteAmount,
          isLocked: false,
          sourceId: null,
        },
      ],
      totals: {
        subtotal: excessCreditNoteAmount,
        totalDiscountAmount: 0,
        taxableAmount: excessCreditNoteAmount,
        totalTaxAmount: 0,
        grandTotal: excessCreditNoteAmount,
      },
      additionalNotes: primaryCreditNote?.additionalNotes ? `${primaryCreditNote.additionalNotes}\n\n--- Excess Credit Note from Invoice ${invoiceNumber} ---` : `Excess Credit Note from Invoice ${invoiceNumber}`,
      termsAndConditions: primaryCreditNote?.termsAndConditions || 'No terms and conditions specified.',
      authorizedSignatureName: primaryCreditNote?.authorizedSignatureName || authorizedSignatureName || '',
      authorizedSignatureImage: primaryCreditNote?.authorizedSignatureImage || authorizedSignature || null,
      userId: userId,
      createdAt: Timestamp.now(),
      status: 'open', // Mark as open
    };

    const creditNotesRef = collection(db, "users", userId, "creditNotes");
    const docRef = await addDoc(creditNotesRef, newCreditNoteData);

    // Step 3: Generate excess credit note PDF using the credit note template
    generateCreditNotePDF({
      businessName,
      businessAddress,
      logo,
      banner,
      creditNoteNumber: newCreditNoteNumber,
      referenceNumber: newCreditNoteData.referenceNumber,
      creditNoteDate: newCreditNoteData.creditNoteDate,
      billTo: newCreditNoteData.billTo,
      items: newCreditNoteData.items,
      totals: newCreditNoteData.totals,
      currencySymbol,
      additionalNotes: newCreditNoteData.additionalNotes,
      termsAndConditions: newCreditNoteData.termsAndConditions,
      authorizedSignatureName: newCreditNoteData.authorizedSignatureName,
      authorizedSignatureImage: newCreditNoteData.authorizedSignatureImage,
      invoiceFooter,
      pdfFileName: `${newCreditNoteNumber}.pdf`,
    });

    console.log("Excess credit note created and applied credit notes closed successfully");
  } catch (error) {
    console.error("Error handling excess credit note:", error);
    alert("Error creating excess credit note: " + error.message);
  }
}

// Generate Excess Credit Note PDF
function generateExcessCreditNotePDF(options) {
  const {
    businessName,
    businessAddress,
    logo,
    invoiceNumber,
    invoiceDate,
    billTo,
    excessCreditNoteAmount,
    currencySymbol,
    invoiceFooter,
    authorizedSignature,
    authorizedSignatureName,
    newCreditNoteNumber,
  } = options;

  const pageHeight = 297;
  const pageWidth = 210;
  const margin = 10;
  const contentWidth = pageWidth - 2 * margin;

  const pdf = new jsPDF();
  let currentY = margin;

  // Add title
  pdf.setFontSize(16).setFont('helvetica', 'bold');
  pdf.text('EXCESS CREDIT NOTE', pageWidth / 2, currentY, { align: 'center' });
  currentY += 10;
  
  // Add credit note number
  pdf.setFontSize(11).setFont('helvetica', 'normal');
  pdf.text(`CN#: ${newCreditNoteNumber}`, pageWidth / 2, currentY, { align: 'center' });
  currentY += 10;

  // Add business info
  if (logo) {
    try {
      pdf.addImage(logo, 'PNG', margin, currentY, 30, 30);
    } catch (e) {
      console.warn('Could not add logo to excess CN PDF');
    }
  }

  pdf.setFontSize(11).setFont('helvetica', 'bold');
  pdf.text(businessName || 'Business Name', margin + 35, currentY);
  pdf.setFontSize(9).setFont('helvetica', 'normal');
  const addressLines = businessAddress ? businessAddress.split('\n') : [];
  addressLines.forEach((line, idx) => {
    pdf.text(line, margin + 35, currentY + 7 + idx * 5);
  });
  currentY += 40;

  // Reference info
  pdf.setFontSize(10).setFont('helvetica', 'bold');
  pdf.text(`Original Invoice: ${invoiceNumber}`, margin, currentY);
  pdf.text(`Issue Date: ${new Date(invoiceDate).toLocaleDateString()}`, margin + contentWidth / 2, currentY);
  currentY += 10;

  // Bill To
  pdf.setFontSize(10).setFont('helvetica', 'bold');
  pdf.text('Bill To:', margin, currentY);
  currentY += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.text(billTo?.name || 'Customer', margin, currentY);
  currentY += 5;
  if (billTo?.address) {
    const addressLines = billTo.address.split('\n');
    addressLines.forEach(line => {
      pdf.text(line, margin, currentY);
      currentY += 5;
    });
  }
  currentY += 5;

  // Reason
  pdf.setFontSize(10).setFont('helvetica', 'bold');
  pdf.text('Reason:', margin, currentY);
  currentY += 6;
  pdf.setFont('helvetica', 'normal');
  const reasonText = `This is an excess credit note generated because the total credit notes applied exceeded the invoice amount.`;
  const wrappedReason = pdf.splitTextToSize(reasonText, contentWidth);
  pdf.text(wrappedReason, margin, currentY);
  currentY += wrappedReason.length * 5 + 10;

  // Amount details table
  const amountTableData = [
    ['Description', 'Amount'],
    ['Excess Credit Note Amount', formatTableCurrency(excessCreditNoteAmount, currencySymbol) + ' ' + currencySymbol]
  ];

  autoTable(pdf, {
    startY: currentY,
    head: [amountTableData[0]],
    body: [amountTableData[1]],
    theme: 'grid',
    headStyles: { fillColor: [255, 152, 0] },
    margin: { left: margin, right: margin },
  });
  currentY = pdf.lastAutoTable.finalY + 10;

  // Signature section
  if (authorizedSignature) {
    pdf.setFontSize(10).setFont('helvetica', 'bold');
    pdf.text('Authorized Signature:', margin, currentY);
    currentY += 8;
    try {
      pdf.addImage(authorizedSignature, 'PNG', margin, currentY, 40, 20);
      currentY += 25;
    } catch (e) {
      console.warn('Could not add signature to excess CN PDF');
      currentY += 10;
    }
  }

  if (authorizedSignatureName) {
    pdf.setFontSize(9).setFont('helvetica', 'normal');
    pdf.text(authorizedSignatureName, margin, currentY);
  }

  // Footer
  if (invoiceFooter) {
    pdf.setFontSize(8).setFont('helvetica', 'normal');
    pdf.text(invoiceFooter, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  // Save the PDF
  const fileName = `${newCreditNoteNumber}.pdf`;
  addPageNumbers(pdf);
  pdf.save(fileName);
}

export function generateQuotationPDF(options) {
  // Destructure all needed fields from options
  const {
    businessName,
    businessAddress,
    logo,
    banner, // Banner property added
    quotationNumber,
    quotationDate,
    validUntil,
    billTo: billToRaw,
    shipTo: shipToRaw,
    items = [],
    totals = {},
    currencySymbol,
    additionalNotes,
    termsAndConditions,
    coveringLetter,
    authorizedSignature,
    authorizedSignatureName,
    customerSignatureName,
    customFooter,
    isSaved,
    setIsPrinting,
  } = options;

  // Helper function to convert currency symbol to code
  const getCurrencyLabel = () => {
    if (currencySymbol === '₹' || currencySymbol === '\u20B9' || currencySymbol === 'Rs') return 'INR';
    if (currencySymbol === '$') return 'USD';
    if (currencySymbol === '€') return 'EUR';
    if (currencySymbol === '£') return 'GBP';
    if (currencySymbol === '¥') return 'JPY';
    return (typeof currencySymbol === 'string' && currencySymbol.length === 3) ? currencySymbol : 'INR';
  };

  // Safe defaults for billTo and shipTo
  // Ensure billTo and shipTo are objects with string fields
  const billTo = {
    name: (billToRaw && typeof billToRaw.name === 'string') ? billToRaw.name : '',
    address: (billToRaw && typeof billToRaw.address === 'string') ? billToRaw.address : ''
  };
  const shipTo = {
    name: (shipToRaw && typeof shipToRaw.name === 'string') ? shipToRaw.name : '',
    address: (shipToRaw && typeof shipToRaw.address === 'string') ? shipToRaw.address : ''
  };
  if (setIsPrinting) setIsPrinting(true);
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let currentY = margin;
  const addSection = (title, text, options = {}) => {
    const { spaceAfter = 8, titleSize = 11, textSize = 10 } = options;
    const plainText = htmlToPlainText(text);
    if (!plainText || !plainText.trim()) return;
    const pageBottomMargin = 25;
    const titleSpacing = 6;
    const titleHeight = (title && title.trim()) ? (pdf.setFontSize(titleSize).getTextDimensions(title).h + titleSpacing) : 0;
    pdf.setFontSize(textSize);
    const textLines = pdf.splitTextToSize(plainText, contentWidth);
    const textBlockHeight = pdf.getTextDimensions(textLines).h;
    const totalSectionHeight = titleHeight + textBlockHeight;
    if (currentY + totalSectionHeight > pageHeight - pageBottomMargin) {
      pdf.addPage();
      currentY = margin;
    }
    if (title && title.trim()) {
      pdf.setFontSize(titleSize).setFont("helvetica", "bold");
      pdf.text(title, margin, currentY);
      currentY += titleSpacing;
    }
    pdf.setFontSize(textSize).setFont("helvetica", "normal");
    pdf.text(textLines, margin, currentY);
    currentY += textBlockHeight + spaceAfter;
  };
  const hasCoveringLetter = coveringLetter && htmlToPlainText(coveringLetter).trim();
  if (hasCoveringLetter) {
    pdf.setFontSize(13).setFont('helvetica', 'bold');
    pdf.text('Covering Letter', margin, currentY);
    currentY += 8;
    pdf.setFontSize(10).setFont('helvetica', 'normal');
    const senderDetails = [
      businessName || "Your Business Name",
      ...(businessAddress ? businessAddress.split('\n') : ["Your Business Address"]),
    ];
    let senderY = currentY;
    senderDetails.forEach(line => {
      pdf.text(line, pageWidth - margin, senderY, { align: 'right' });
      senderY += 5;
    });
    currentY = Math.max(currentY + 25, senderY);
    pdf.text(new Date(quotationDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }), margin, currentY);
    currentY += 15;
    pdf.setFontSize(10).setFont('helvetica', 'bold');
    pdf.text("To,", margin, currentY);
    currentY += 5;
    pdf.setFont('helvetica', 'normal');
    const recipientDetails = [
      billTo.name,
      ...(billTo.address ? billTo.address.split('\n') : [])
    ];
    recipientDetails.forEach(line => {
      if (line.trim()) {
        pdf.text(line, margin, currentY);
        currentY += 5;
      }
    });
    currentY += 10;
    pdf.setFontSize(10).setFont('helvetica', 'bold');
    pdf.text("Subject: Submission of Quotation", margin, currentY);
    currentY += 15;
    pdf.setFont('helvetica', 'normal');
    const recipientName = billTo.name.split('\n')[0];
    pdf.text(`Dear ${recipientName || 'Sir/Madam'},`, margin, currentY);
    currentY += 10;
    const plainTextLetter = htmlToPlainText(coveringLetter);
    const letterLines = pdf.splitTextToSize(plainTextLetter, contentWidth);
    pdf.text(letterLines, margin, currentY);
    currentY += letterLines.length * 5 + 10;
    pdf.text("Sincerely,", margin, currentY);
    currentY += 10;
    if (authorizedSignature) {
      try {
        pdf.addImage(authorizedSignature, 'PNG', margin, currentY, 40, 20);
        currentY += 25;
      } catch(e) { console.error("Could not add signature to covering letter", e); }
    }
    pdf.setFont('helvetica', 'bold');
    pdf.text(authorizedSignatureName || "Authorized Signatory", margin, currentY);
    currentY += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.text(businessName || "Your Business Name", margin, currentY);
    pdf.addPage();
    currentY = margin;
  }
  
  // === MODIFIED HEADER LOGIC STARTS HERE ===
  pdf.setFontSize(18).setFont("helvetica", "bold");
  pdf.text("QUOTATION", pageWidth / 2, currentY, { align: 'center' });
  currentY += 10;

  if (banner && banner.length > 0 && typeof banner === 'string') {
    try {
        console.log("Banner found, attempting to add to PDF");
        const bannerHeight = 40; // Height of banner in mm
        const bannerWidth = contentWidth;
        
        // Detect image format based on data URL
        let imageFormat = 'PNG'; // Default format
        if (banner.includes('data:image/jpeg') || banner.includes('data:application/jpeg')) {
            imageFormat = 'JPEG';
        } else if (banner.includes('data:image/png')) {
            imageFormat = 'PNG';
        } else if (banner.includes('data:image/webp')) {
            imageFormat = 'WEBP';
        } else if (banner.includes('data:image/')) {
            // Extract format from data URL
            const match = banner.match(/data:image\/([^;]+)/);
            if (match) {
                imageFormat = match[1].toUpperCase();
            }
        }
        
        console.log("Adding banner with format:", imageFormat);
        pdf.addImage(banner, imageFormat, margin, currentY, bannerWidth, bannerHeight);
        console.log("Banner added successfully");
        currentY += bannerHeight + 5;
    } catch (e) {
        console.error("Error adding banner to PDF:", e);
        console.log("Banner length:", banner ? banner.length : 0);
    }
    // Add business name and address below the banner only if they are provided
    if (businessName && businessName.trim()) {
      pdf.setFontSize(11).setFont("helvetica", "bold");
      pdf.text(String(businessName), margin, currentY);
      currentY += 6;
    }
    if (businessAddress && businessAddress.trim()) {
      pdf.setFontSize(8).setFont("helvetica", "normal");
      const addressLines = pdf.splitTextToSize(businessAddress, contentWidth / 2);
      pdf.text(addressLines.map(line => String(line)), margin, currentY);
      currentY += addressLines.length * 4 + 5;
    } else {
      currentY += 3; // Add minimal spacing if no address
    }

  } else {
    // Fallback to logo and right-aligned business details if no banner
    const headerY = currentY;
    let logoHeight = 0;
    if (logo) {
        try {
            const imgProps = pdf.getImageProperties(logo);
            const aspectRatio = imgProps.height / imgProps.width;
            const logoWidth = 24;
            logoHeight = logoWidth * aspectRatio;
            pdf.addImage(logo, 'PNG', margin, headerY, logoWidth, logoHeight);
        } catch (e) {
            console.error("Error adding logo to PDF:", e);
        }
    }
    pdf.setFontSize(11).setFont("helvetica", "bold");
    pdf.text(String(businessName || "Your Company"), pageWidth - margin, headerY + 5, { align: 'right' });
    let businessDetails = businessAddress || "Your Address";
    pdf.setFontSize(8).setFont("helvetica", "normal");
    const businessAddressMaxWidth = 70;
    const addressLines = pdf.splitTextToSize(businessDetails, businessAddressMaxWidth);
    const addressTextHeight = addressLines.length * 4; 
    pdf.text(addressLines.map(line => String(line)), pageWidth - margin, headerY + 12, { align: 'right' });
    const businessTextHeight = addressTextHeight + 8;
    currentY = headerY + Math.max(logoHeight, businessTextHeight) + 10;
  }
  // === MODIFIED HEADER LOGIC ENDS HERE ===

  const detailsBlockY = currentY;
  const colW = (contentWidth / 3) - 5;
  const colGap = 10;
  const col1X = margin;
  const col2X = col1X + colW + colGap;
  const col3X = col2X + colW + colGap;
  pdf.setFontSize(9).setFont("helvetica", "bold");
  pdf.text("Bill To:", col1X, detailsBlockY);
  pdf.setFont("helvetica", "normal");
  const billToLines = pdf.splitTextToSize(`${billTo.name || ''}\n${billTo.address || ''}`, colW);
  pdf.text(billToLines.map(line => String(line)), col1X, detailsBlockY + 5);
  const billToHeight = billToLines.length * 5 + 10;
  pdf.setFontSize(9).setFont("helvetica", "bold");
  pdf.text("Ship To:", col2X, detailsBlockY);
  pdf.setFont("helvetica", "normal");
  const shipToLines = pdf.splitTextToSize(`${shipTo.name || ''}\n${shipTo.address || ''}`, colW);
  pdf.text(shipToLines.map(line => String(line)), col2X, detailsBlockY + 5);
  const shipToHeight = shipToLines.length * 5 + 10;
  let metaY = detailsBlockY;
  const metaLabelGap = 28;
  pdf.setFontSize(9).setFont("helvetica", "bold");
  pdf.text("Quotation ID:", col3X, metaY);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(quotationNumber || ''), col3X + metaLabelGap, metaY);
  metaY += 5;
  pdf.setFont("helvetica", "bold");
  pdf.text("Date:", col3X, metaY);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(quotationDate || ''), col3X + metaLabelGap, metaY);
  metaY += 5;
  pdf.setFont("helvetica", "bold");
  pdf.text("Valid Until:", col3X, metaY);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(validUntil || ''), col3X + metaLabelGap, metaY);
  metaY += 5;
  pdf.setFont("helvetica", "bold");
  pdf.text("Currency:", col3X, metaY);
  pdf.setFont("helvetica", "normal");
  const currencyLabel = getCurrencyLabel();
  pdf.text(String(currencyLabel || 'INR'), col3X + metaLabelGap, metaY);
  metaY += 5;
  const metaHeight = metaY - detailsBlockY + 10;
  const blockHeight = Math.max(billToHeight, shipToHeight, metaHeight) + 5;
  currentY = detailsBlockY + blockHeight;
  
  // Check if any item has a discount
  const hasAnyDiscount = items.some(item => item.discount && parseFloat(item.discount) > 0);
  
  // Build table headers dynamically based on whether discount exists
  const tableHeadRow = ["Product Name", "Qty", `Unit Price (${currencyLabel})`];
  if (hasAnyDiscount) {
    tableHeadRow.push("Discount");
  }
  tableHeadRow.push("Tax", `Total (${currencyLabel})`);
  const tableHead = [tableHeadRow];
  
  // Build table body with conditional discount column
  const tableBody = items.map(item => {
    const row = [
      item.description,
      item.quantity,
      formatTableCurrency(item.unitPrice, currencySymbol),
    ];
    if (hasAnyDiscount) {
      row.push(`${item.discount}%`);
    }
    row.push(`${item.tax}%`, formatTableCurrency(item.total, currencySymbol));
    return row;
  });
  
  // Build total rows with conditional discount column
  const subtotalRow = ['', '', ''];
  if (hasAnyDiscount) {
    subtotalRow.push('');
  }
  subtotalRow.push('Subtotal:', formatTableCurrency(totals.subtotal, currencySymbol));
  
  const gstRow = ['', '', ''];
  if (hasAnyDiscount) {
    gstRow.push('');
  }
  gstRow.push('Total GST:', formatTableCurrency(totals.totalTaxAmount, currencySymbol));
  
  const grandTotalRow = ['', '', ''];
  if (hasAnyDiscount) {
    grandTotalRow.push('');
  }
  grandTotalRow.push('Grand Total:', `${formatTableCurrency(totals.grandTotal, currencySymbol)}`);
  
  const fullTableBody = [...tableBody, subtotalRow, gstRow, grandTotalRow];
  autoTable(pdf, {
    startY: currentY,
    head: tableHead,
    body: fullTableBody,
    theme: 'grid',
    headStyles: { fillColor: [38, 100, 200], textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: margin, right: margin },
    didParseCell: function(data) {
      const isTotalRow = data.row.index >= items.length;
      if (isTotalRow) {
        // Column indices need to be adjusted based on whether discount column exists
        const priceColIndex = hasAnyDiscount ? 5 : 4;
        const totalColIndex = hasAnyDiscount ? 6 : 5;
        if (data.column.index === priceColIndex || data.column.index === totalColIndex) {
          data.cell.styles.halign = 'right';
        }
        if (data.row.index === fullTableBody.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [38, 100, 200];
          data.cell.styles.fillColor = [230, 240, 255];
        }
      }
    }
  });
  currentY = pdf.lastAutoTable.finalY + 10;
  // Add grand total in words before the summary, right-aligned and not bold
  if (typeof totals.grandTotal === 'number' && !isNaN(totals.grandTotal)) {
    const grandTotalWords = numberToRupeesPaiseWords(totals.grandTotal);
    // Capitalize first letter of every word
    const grandTotalWordsTitleCase = grandTotalWords.replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1));
    pdf.setFontSize(11).setFont('helvetica', 'normal');
    const fullWordsText = `Grand Total (in words): ${grandTotalWordsTitleCase} Only`;
    const wrappedLines = pdf.splitTextToSize(fullWordsText, contentWidth);
    const blockHeight = pdf.getTextDimensions(wrappedLines).h || (wrappedLines.length * 6);
    const pageBottomMargin = 25;
    if (currentY + blockHeight > pageHeight - pageBottomMargin) {
      pdf.addPage();
      currentY = margin;
    }
    const lineHeight = blockHeight / wrappedLines.length;
    wrappedLines.forEach(line => {
      pdf.text(line, pageWidth - margin, currentY, { align: 'right' });
      currentY += lineHeight;
    });
    currentY += 2;
  }
  // Render summary table with only required columns for Quotation
  if (Array.isArray(options.summary) && options.summary.length > 0) {
    // Extract specific values from summary for cleaner table
    const getValueFromSummary = (labels) => {
      for (let label of labels) {
        const item = options.summary.find(s => s.label.toLowerCase().includes(label.toLowerCase()));
        if (item) {
          let value = String(item.value);
          value = value.replace(/([\u20b9$\u20ac\u00a3\u00a5]|INR|USD|EUR|GBP|JPY|-)/gi, '').trim();
          return value;
        }
      }
      return '0';
    };

    const summaryHead = [['Discount', 'Taxable Amt (INR)', 'CGST', 'SGST', 'IGST', 'Net Total (INR)']];
    const summaryBody = [[
      getValueFromSummary(['discount']),
      getValueFromSummary(['taxable']),
      getValueFromSummary(['cgst']),
      getValueFromSummary(['sgst']),
      getValueFromSummary(['igst']),
      getValueFromSummary(['net total', 'grand total'])
    ]];

    autoTable(pdf, {
      startY: currentY,
      head: summaryHead,
      body: summaryBody,
      theme: 'grid',
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 4, halign: 'left', valign: 'middle' },
      headStyles: { fillColor: [63, 81, 181], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'left', valign: 'middle' },
      bodyStyles: { fontStyle: 'normal', halign: 'left', valign: 'middle' },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { halign: 'left' },
        4: { halign: 'left' },
        5: { halign: 'left' },
      },
    });
    currentY = pdf.lastAutoTable.finalY + 8;
  }
// Helper to convert number to words with currency
function numberToWordsWithCurrency(amount, currencyLabel) {
  const rounded = Math.round(amount);
  const words = numberToWords(rounded);
  return `${words.charAt(0).toUpperCase() + words.slice(1)} ${currencyLabel}`;
}

// Simple number to words (supports up to 99999999)
// explain the working......
function numberToWords(num) {
  const a = [ '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen' ];
  const b = [ '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety' ];
  const g = [ '', 'thousand', 'lakh', 'crore' ];
  if (num === 0) return 'zero';
  let str = '';
  let i = 0;
  while (num > 0) {
    let chunk = num % 1000;
    if (chunk) {
      let chunkStr = '';
      if (chunk > 99) {
        chunkStr += a[Math.floor(chunk / 100)] + ' hundred ';
        chunk %= 100;
      }
      if (chunk > 19) {
        chunkStr += b[Math.floor(chunk / 10)] + ' ' + a[chunk % 10] + ' ';
      } else if (chunk > 0) {
        chunkStr += a[chunk] + ' ';
      }
      str = chunkStr + g[i] + ' ' + str;
    }
    num = Math.floor(num / 1000);
    i++;
  }
  return str.replace(/ +/g, ' ').trim();
}
  addSection("Additional Notes", additionalNotes, { spaceAfter: 4 });
  addSection("Terms and Conditions", termsAndConditions, { spaceAfter: 15 });
  // Only show signature section if at least one signature is present
  const showSignatureSection = authorizedSignature || (customerSignatureName && customerSignatureName.trim());
  if (showSignatureSection) {
    const signatureSectionHeight = 55;
    if (currentY > pageHeight - signatureSectionHeight - 20) { 
      pdf.addPage();
      currentY = margin;
    }
    pdf.setFontSize(10).setFont("helvetica", "bold");
    pdf.text("Signatures", margin, currentY);
    currentY += 10;
    const sigWidth = 60;
    const sigHeight = 30;
    const signatureY = currentY + 5; 
    if (authorizedSignature) {
      try {
        pdf.addImage(authorizedSignature, 'PNG', margin, signatureY, sigWidth, sigHeight);
        pdf.setLineWidth(0.2).line(margin, signatureY + sigHeight, margin + sigWidth, signatureY + sigHeight);
        pdf.setFontSize(8).setFont("helvetica", "normal");
        pdf.text(authorizedSignatureName || "Authorized Signature", margin + sigWidth / 2, signatureY + sigHeight + 5, { align: 'center' });
      } catch (e) {
        console.error("Could not add authorized signature to PDF:", e);
        pdf.setFontSize(9).setFont("helvetica", "italic");
        pdf.text("Error rendering authorized signature.", margin, signatureY + 15);
      }
    }
    if (customerSignatureName && customerSignatureName.trim()) {
      const customerSigX = pageWidth - margin - sigWidth;
      pdf.setLineWidth(0.2).line(customerSigX, signatureY + sigHeight, customerSigX + sigWidth, signatureY + sigHeight);
      pdf.setFontSize(8).setFont("helvetica", "normal");
      pdf.text(customerSignatureName, customerSigX + sigWidth / 2, signatureY + sigHeight + 5, { align: 'center' });
    }
    currentY += signatureSectionHeight - 10;
  }
  const plainFooterText = htmlToPlainText(customFooter);
  if (plainFooterText && plainFooterText.trim()) {
    const pageBottomMargin = 25;
    const dividerHeight = 5; 
    pdf.setFontSize(9);
    const footerTextHeight = pdf.getTextDimensions(pdf.splitTextToSize(plainFooterText, contentWidth)).h;
    if (currentY + dividerHeight + footerTextHeight > pageHeight - pageBottomMargin) {
      pdf.addPage();
      currentY = margin;
    }
    currentY += 10;
    pdf.setDrawColor("#cccccc");
    pdf.setLineWidth(0.2).line(margin, currentY, pageWidth - margin, currentY);
    currentY += 8;
    addSection("", customFooter, { spaceAfter: 0 }); 
  }

  // Add formatted timestamp footer with timezone
  const formattedTimestamp = getFormattedTimestamp();
  pdf.setFontSize(7).setFont("helvetica", "italic");
  pdf.text(`Generated on: ${formattedTimestamp}`, margin, pageHeight - 10);
  pdf.text("Powered by OmneVu.com", pageWidth - margin, pageHeight - 10, { align: 'right' });
  pdf.setFont("helvetica", "normal");
  if (options && options.returnBlob) {
    // Return a Blob for preview
    return pdf.output('blob');
  } else {
    addPageNumbers(pdf);
    pdf.save(`${quotationNumber}.pdf`);
    if (setIsPrinting) setIsPrinting(false);
    return pdf;
  }
}

// --- CREDIT NOTE PDF GENERATOR ---
export const generateCreditNotePDF = (options = {}) => {
  const {
    businessName = "",
    businessAddress = "",
    logo = "",
    banner = "",
    creditNoteNumber = "CN-001",
    referenceNumber = "",
    creditNoteDate = "",
    billTo = { name: "", address: "" },
    items = [],
    totals = { subtotal: 0, totalDiscountAmount: 0, taxableAmount: 0, totalTaxAmount: 0, grandTotal: 0 },
    currencySymbol = "₹",
    additionalNotes = "",
    termsAndConditions = "",
    authorizedSignatureName = "",
    authorizedSignatureImage = null,
    invoiceFooter = "",
    setIsPrinting = null,
    pdfFileName = "credit-note.pdf",
  } = options;

  // Helper function to convert currency symbol to code
  const getCurrencyLabel = () => {
    if (currencySymbol === '₹' || currencySymbol === '\u20B9' || currencySymbol === 'Rs') return 'INR';
    if (currencySymbol === '$') return 'USD';
    if (currencySymbol === '€') return 'EUR';
    if (currencySymbol === '£') return 'GBP';
    if (currencySymbol === '¥') return 'JPY';
    return (typeof currencySymbol === 'string' && currencySymbol.length === 3) ? currencySymbol : 'INR';
  };

  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;
  let currentY = margin;

  // Add Credit Note Header at the top
  pdf.setFontSize(16).setFont("helvetica", "bold");
  pdf.text("CREDIT NOTE", pageWidth / 2, currentY, { align: 'center' });
  currentY += 12;

  // Add Banner Section
  if (banner) {
    try {
      const bannerHeight = 40;
      const imageFormat = banner.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
      pdf.addImage(banner, imageFormat, margin, currentY, contentWidth, bannerHeight);
      currentY += bannerHeight + 5;

      if (businessName && businessName.trim()) {
        pdf.setFontSize(9).setFont("helvetica", "bold");
        pdf.text(businessName, margin, currentY);
        currentY += 6;
      }
      if (businessAddress && businessAddress.trim()) {
        pdf.setFontSize(7).setFont("helvetica", "normal");
        const addressLines = pdf.splitTextToSize(businessAddress, contentWidth / 2);
        pdf.text(addressLines, margin, currentY);
        currentY += addressLines.length * 3 + 5;
      }
    } catch (e) {
      console.error("Could not add banner to PDF:", e);
    }
  }

  // Credit Note Details - Three column layout
  const detailsBlockY = currentY;
  const colW = (contentWidth / 3) - 5;
  const colGap = 10;
  const col1X = margin;
  const col2X = col1X + colW + colGap;
  const col3X = col2X + colW + colGap;

  pdf.setFontSize(9).setFont("helvetica", "bold");
  pdf.text("Credit Note #:", col1X, detailsBlockY);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(creditNoteNumber), col1X, detailsBlockY + 5);

  pdf.setFont("helvetica", "bold");
  pdf.text("Date:", col2X, detailsBlockY);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(creditNoteDate), col2X, detailsBlockY + 5);

  pdf.setFont("helvetica", "bold");
  pdf.text("Currency:", col3X, detailsBlockY);
  pdf.setFont("helvetica", "normal");
  const currencyLabel = getCurrencyLabel();
  pdf.text(String(currencyLabel || 'INR'), col3X, detailsBlockY + 5);

  // Add Reference # and Date on second row
  pdf.setFont("helvetica", "bold");
  pdf.text("Reference #:", col1X, detailsBlockY + 12);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(referenceNumber || ""), col1X, detailsBlockY + 17);

  currentY = detailsBlockY + 25;

  // Bill To Section
  pdf.setFontSize(9).setFont("helvetica", "bold");
  pdf.text("Bill To:", margin, currentY);
  currentY += 6;
  pdf.setFontSize(8).setFont("helvetica", "normal");
  if (billTo.name) {
    pdf.text(billTo.name, margin + 5, currentY);
    currentY += 5;
  }
  if (billTo.address) {
    const addressLines = pdf.splitTextToSize(billTo.address, contentWidth - 10);
    pdf.text(addressLines, margin + 5, currentY);
    currentY += addressLines.length * 4 + 3;
  }
  currentY += 3;

  // Check if any item has discount
  const hasAnyDiscount = items.some(item => item.discount && parseFloat(item.discount) > 0);

  // Items Table Header - dynamic based on discount existence
  const tableHeadRow = ["Description", "Qty", `Unit Price (${currencyLabel})`];
  if (hasAnyDiscount) {
    tableHeadRow.push("Discount %");
  }
  tableHeadRow.push("Tax %", `Total (${currencyLabel})`);

  // Build table data
  const tableData = items.map(item => {
    const row = [
      item.description || "",
      parseFloat(item.quantity || 0),
      formatTableCurrency(parseFloat(item.unitPrice) || 0, currencySymbol),
    ];
    if (hasAnyDiscount) {
      row.push(`${parseFloat(item.discount || 0)}`);
    }
    row.push(`${parseFloat(item.tax || 0)}`, formatTableCurrency(parseFloat(item.total) || 0, currencySymbol));
    return row;
  });

  // Add totals rows with proper column alignment
  const createTotalRow = (label, value) => {
    const row = ["", ""];
    if (hasAnyDiscount) {
      row.push("");
    } else {
      row.push("");
    }
    if (hasAnyDiscount) {
      row.push("");
    }
    row.push(label, value);
    return row;
  };

  tableData.push(createTotalRow("Subtotal:", formatTableCurrency(parseFloat(totals.subtotal) || 0, currencySymbol)));

  if (hasAnyDiscount) {
    tableData.push(createTotalRow("Total Discount:", formatTableCurrency(parseFloat(totals.totalDiscountAmount) || 0, currencySymbol)));
  }

  tableData.push(createTotalRow("Total Tax:", formatTableCurrency(parseFloat(totals.totalTaxAmount) || 0, currencySymbol)));
  tableData.push(createTotalRow("Grand Total:", formatTableCurrency(parseFloat(totals.grandTotal) || 0, currencySymbol)));

  // Generate Table
  autoTable(pdf, {
    head: [tableHeadRow],
    body: tableData,
    startY: currentY,
    margin: { left: margin, right: margin },
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [63, 81, 181], textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    didParseCell: (data) => {
      if (data.row.index >= items.length) {
        data.cell.styles.fontStyle = 'bold';
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.textColor = [63, 81, 181];
          data.cell.styles.fillColor = [230, 240, 255];
        }
      }
    },
    columnStyles: hasAnyDiscount ? {
      0: { halign: 'left' },
      1: { halign: 'center' },
      2: { halign: 'right' },
      3: { halign: 'center' },
      4: { halign: 'center' },
      5: { halign: 'right' },
    } : {
      0: { halign: 'left' },
      1: { halign: 'center' },
      2: { halign: 'right' },
      3: { halign: 'center' },
      4: { halign: 'right' },
    },
  });

  currentY = pdf.lastAutoTable.finalY + 10;

  // Calculate CGST and SGST (assuming equal split of total tax)
  const cgstAmount = parseFloat(totals.totalTaxAmount || 0) / 2;
  const sgstAmount = parseFloat(totals.totalTaxAmount || 0) / 2;
  const igstAmount = 0; // For credit notes, typically CGST and SGST apply, not IGST

  // Summary Table with only required columns
  const summaryHead = [["Discount", "Taxable Amt (INR)", "CGST", "SGST", "IGST", "Net Total (INR)"]];
  const summaryBody = [[
    formatTableCurrency(parseFloat(totals.totalDiscountAmount) || 0, currencySymbol),
    formatTableCurrency(parseFloat(totals.taxableAmount) || 0, currencySymbol),
    formatTableCurrency(cgstAmount, currencySymbol),
    formatTableCurrency(sgstAmount, currencySymbol),
    formatTableCurrency(igstAmount, currencySymbol),
    formatTableCurrency(parseFloat(totals.grandTotal) || 0, currencySymbol),
  ]];

  autoTable(pdf, {
    head: summaryHead,
    body: summaryBody,
    startY: currentY,
    margin: { left: margin, right: margin },
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, halign: 'left', valign: 'middle' },
    headStyles: { fillColor: [63, 81, 181], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'left', valign: 'middle' },
    bodyStyles: { fontStyle: 'normal', halign: 'left', valign: 'middle' },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'left' },
      2: { halign: 'left' },
      3: { halign: 'left' },
      4: { halign: 'left' },
      5: { halign: 'left' },
    },
  });

  currentY = pdf.lastAutoTable.finalY + 10;

  // Additional Notes
  if (additionalNotes && additionalNotes.trim()) {
    pdf.setFontSize(9).setFont("helvetica", "bold");
    pdf.text("Additional Notes:", margin, currentY);
    currentY += 6;
    pdf.setFontSize(8).setFont("helvetica", "normal");
    const notesText = pdf.splitTextToSize(additionalNotes, contentWidth);
    pdf.text(notesText, margin, currentY);
    currentY += notesText.length * 4 + 5;
  }

  // Terms and Conditions
  if (termsAndConditions && termsAndConditions.trim()) {
    pdf.setFontSize(9).setFont("helvetica", "bold");
    pdf.text("Terms and Conditions:", margin, currentY);
    currentY += 6;
    pdf.setFontSize(7).setFont("helvetica", "normal");
    const tncText = pdf.splitTextToSize(termsAndConditions, contentWidth);
    pdf.text(tncText, margin, currentY);
    currentY += tncText.length * 3 + 5;
  }

  // Signature Section
  if (authorizedSignatureImage || authorizedSignatureName) {
    currentY += 10;
    const sigWidth = 40;
    const sigHeight = 20;

    if (authorizedSignatureImage) {
      const signatureY = currentY;
      try {
        const imageFormat = authorizedSignatureImage.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
        pdf.addImage(authorizedSignatureImage, imageFormat, margin, signatureY, sigWidth, sigHeight);
        pdf.setLineWidth(0.2).line(margin, signatureY + sigHeight, margin + sigWidth, signatureY + sigHeight);
        pdf.setFontSize(8).setFont("helvetica", "normal");
        pdf.text(authorizedSignatureName || "Authorized Signature", margin + sigWidth / 2, signatureY + sigHeight + 5, { align: 'center' });
        currentY += sigHeight + 20;
      } catch (e) {
        console.error("Could not add authorized signature to PDF:", e);
      }
    } else if (authorizedSignatureName) {
      pdf.setFontSize(8).setFont("helvetica", "normal");
      pdf.text(authorizedSignatureName, margin, currentY + 15);
      currentY += 25;
    }
  }

  // Footer
  const formattedTimestamp = getFormattedTimestamp();
  pdf.setFontSize(7).setFont("helvetica", "italic");
  pdf.text(`Generated on: ${formattedTimestamp}`, margin, pageHeight - 10);
  pdf.text("Powered by OmneVu.com", pageWidth - margin, pageHeight - 10, { align: 'right' });
  pdf.setFont("helvetica", "normal");

  addPageNumbers(pdf);
  pdf.save(pdfFileName);
  if (setIsPrinting) setIsPrinting(false);
  return pdf;
};